import { ClientBase } from "./base.js";
import { ApiError } from "../errors.js";
import { MosqueClient } from "./mosque.js";
/** 409 punch_sequence from the timeclock; `allowed` lists the kinds valid right now. */
export class PunchSequenceError extends ApiError {
    allowed;
    constructor(message, allowed) {
        super(409, "punch_sequence", message);
        this.name = "PunchSequenceError";
        this.allowed = allowed;
    }
}
/** Dine client (#1084): guest table ordering, cash settle, kitchen line. */
export class DineClient extends MosqueClient {
    /** Public. A device (kiosk/board/kitchen) asks for a pairing code; staff bind it in the portal. */
    async startEdgeClaim(firmwareVersion = "", role) {
        return this.request("POST", "/v1/edge/claim", role ? { firmwareVersion, role } : { firmwareVersion });
    }
    /** Public, poll-secret gated. 204 -> pending; 200 -> claimed (device token delivered once). */
    async pollEdgeClaim(claimId, pollToken) {
        const res = await this.request("GET", `/v1/edge/claim/${encodeURIComponent(claimId)}/poll`, undefined, false, false, { "X-Lumora-Claim-Poll": pollToken });
        if (!res)
            return { status: "pending" };
        return {
            status: "claimed",
            deviceId: res.deviceId,
            token: res.token,
            ...(res.role ? { role: res.role } : {}),
            ...(res.tenantSlug ? { tenantSlug: res.tenantSlug } : {}),
            ...(res.locationId ? { locationId: res.locationId } : {}),
        };
    }
    /** Device bearer (#1198): which tenant/branch/role this paired screen is. 401 when revoked. */
    async edgeWhoAmI(deviceToken) {
        return this.request("GET", "/v1/edge/whoami", undefined, false, false, {
            Authorization: `Bearer ${deviceToken}`,
        });
    }
    /** Public (#1198). 404 `dine_config_missing` until the owner saves one via PUT /content. */
    async getDineConfig(slug) {
        return this.request("GET", `${this.siteBase(slug)}/dine-config`);
    }
    /** Public. The token travels in the body, never the URL. 404 for unknown/rotated/revoked. */
    async resolveTableTag(token) {
        return this.request("POST", "/v1/dining/tags/resolve", { token });
    }
    /** Public. Starts (201) or joins (200, joined:true) the anonymous table session. */
    async startTableSession(token) {
        return this.request("POST", "/v1/dining/tags/session", { token });
    }
    /**
     * POSTs the existing checkout route with `payCash: true`; answers with the
     * backend CashPlacementJSON shape (cashCode, expiresAt).
     */
    async placeCashOrder(slug, input, opts = {}) {
        return this.request("POST", `${this.siteBase(slug)}/checkout`, { ...input, payCash: true }, false, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Staff (orders fulfill/manage). 404 cash_code_invalid, 429 rate_limited. */
    async settleCash(code) {
        return this.request("POST", "/v1/orders/settle-cash", { code }, true);
    }
    /** Staff (orders:fulfill). Queues a drawer kick on the paired printer. 404 other tenant, 409 no_printer. */
    async kickDrawer(orderId) {
        await this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/drawer-kick`, undefined, true);
    }
    /** Staff (orders:fulfill). Queues a cash receipt print. 404 other tenant, 409 no_printer. */
    async printReceipt(orderId) {
        await this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/print-receipt`, undefined, true);
    }
    /**
     * Staff POS (kitchen auth + orders fulfill/manage). The server prices the order.
     * Pass `idempotencyKey` so a double-submit returns the same order. `pay: "card"` answers
     * 501 `not_implemented`; invalid items/modifiers/caps answer 400.
     */
    async placePosOrder(input, opts = {}) {
        return this.request("POST", `/v1/orders/pos${ClientBase.query({ location: opts.location })}`, input, true, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Prices a POS ticket on the server with the placement code path and stores nothing (QA-036b). */
    async quotePosOrder(input, opts = {}) {
        return this.request("POST", `/v1/orders/pos/quote${ClientBase.query({ location: opts.location })}`, input, true);
    }
    /** Staff POS menu with modifier groups, options and price deltas (display only). */
    async getPosCatalog(location) {
        return this.request("GET", `/v1/orders/pos/catalog${ClientBase.query({ location })}`, undefined, true);
    }
    async getKitchenLineStages(location) {
        return this.request("GET", `/v1/kitchen/line/stages${ClientBase.query({ location })}`, undefined, true);
    }
    async listKitchenLineTickets(opts = {}) {
        return this.request("GET", `/v1/kitchen/line/tickets${ClientBase.query({
            location: opts.location,
            include_ready: opts.includeReady ? "1" : undefined,
        })}`, undefined, true);
    }
    /** `fromStage` guards against a ticket that already moved (409 conflict). */
    async bumpKitchenTicket(orderId, opts = {}) {
        return this.kitchenAct(orderId, "bump", opts);
    }
    async unbumpKitchenTicket(orderId, opts = {}) {
        return this.kitchenAct(orderId, "unbump", opts);
    }
    async markKitchenTicketReady(orderId, opts = {}) {
        return this.kitchenAct(orderId, "ready", opts);
    }
    kitchenAct(orderId, action, opts) {
        const body = {};
        if (opts.fromStage)
            body["from_stage"] = opts.fromStage;
        if (opts.location !== undefined)
            body["location"] = opts.location;
        return this.request("POST", `/v1/kitchen/line/tickets/${encodeURIComponent(orderId)}/${action}`, body, true);
    }
    // ---- Stripe Terminal card payments (#1107): staff / kitchen-PIN / device token ----
    /** Starts a reader payment on a held order. 201 new, 200 replay of the same Idempotency-Key. */
    async startTerminalPayment(slug, orderId, input = {}, opts = {}) {
        return this.request("POST", `${this.siteBase(slug)}/orders/${encodeURIComponent(orderId)}/terminal-payments`, input, true, false, opts.idempotencyKey ? { "Idempotency-Key": opts.idempotencyKey } : undefined);
    }
    /** Stored state only; poll every 1-2s while processing. */
    async getTerminalPayment(slug, paymentId) {
        return this.request("GET", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}`, undefined, true);
    }
    /**
     * 409 terminal_reader_busy when the reader is mid-authorization. `void: true` also voids the PaymentIntent so an
     * abandoned card payment can never be charged later (QA-036); a plain cancel keeps it for a retry.
     */
    async cancelTerminalPayment(slug, paymentId, opts = {}) {
        return this.request("POST", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}/cancel`, opts.void ? { void: true } : undefined, true);
    }
    /** Sandbox only (404 outside Stripe test mode). Taps a Stripe test card on the simulated reader, then answers the payment. */
    async simulateTerminalPayment(slug, paymentId, input = {}) {
        return this.request("POST", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}/simulate-present`, input, true);
    }
    /** Staff. `testMode` is true when the backend holds a Stripe test key (simulated reader and test cards available). */
    async getTerminalStatus(slug) {
        return this.request("GET", `${this.siteBase(slug)}/terminal/status`, undefined, true);
    }
    async retryTerminalPayment(slug, paymentId, input = {}) {
        return this.request("POST", `${this.siteBase(slug)}/terminal-payments/${encodeURIComponent(paymentId)}/retry`, input, true);
    }
    /** Owner or platform admin only (billing:manage). */
    async listTerminalReaders(slug) {
        const r = await this.request("GET", `${this.siteBase(slug)}/terminal/readers`, undefined, true);
        return r.readers ?? [];
    }
    // ---- Kiosk Help (QA-073) ----
    /**
     * Kiosk device token. Raises a help alert on the counter tablet. 429 `rate_limited` when this device asked less
     * than 10 s ago. An unknown or foreign `tableToken` is ignored (the alert just has no table).
     */
    async requestKioskHelp(slug, input = {}) {
        return this.request("POST", `${this.siteBase(slug)}/kiosks/help`, input, true);
    }
    /** Staff or the counter device. Clears the help request on every counter. 404 for an unknown id. */
    async ackKioskHelp(slug, id) {
        return this.request("POST", `${this.siteBase(slug)}/kiosks/help/${encodeURIComponent(id)}/ack`, undefined, true);
    }
    // ---- Counter cash tender (#1335) ----
    /**
     * Staff (same capability as settle-cash). Atomic tender with change. A no-op loser answers 200 with
     * `applied: false` and a `reason`. 400 short tender, 404 unknown order, 409 card payment in progress.
     */
    async tenderCash(input) {
        return this.request("POST", "/v1/orders/tender-cash", input, true);
    }
    // ---- Manual marketplace order + reprice to card (#1337, #1338) ----
    /**
     * Staff. Keys in a DoorDash / Uber Eats order the marketplace already paid (prepaid, tender `marketplace`).
     * A repeat of the same (source, externalOrderId) answers 409 `duplicate_order` with the existing order; that
     * is returned as `{ duplicate: true, order }` instead of thrown. 400 unknown field / bad input.
     */
    async createMarketplaceOrder(input) {
        try {
            const order = await this.request("POST", "/v1/orders/marketplace", input, true);
            return { duplicate: false, order };
        }
        catch (e) {
            if (e instanceof ApiError && e.status === 409 && e.code === "duplicate_order") {
                const body = e.body;
                if (body?.order)
                    return { duplicate: true, order: body.order };
            }
            throw e;
        }
    }
    /**
     * Staff. Reprices a held cash order (rank 10) to the card price after the customer chose card. 409 stale
     * `expectedRev`, 400 once the order is past rank 10.
     */
    async repriceCard(input) {
        return this.request("POST", "/v1/orders/reprice-card", input, true);
    }
    // ---- Stripe Reader M2, SDK-driven (#1343): staff / kitchen-PIN token ----
    /** Short-lived Terminal SDK connection token. locationId must be the tenant's own (else 404). */
    async createTerminalConnectionToken(locationId) {
        return this.request("POST", "/v1/terminal/connection-token", locationId ? { locationId } : {}, true);
    }
    async listTerminalLocations() {
        const r = await this.request("GET", "/v1/terminal/locations", undefined, true);
        return r.locations ?? [];
    }
    /** Owner (billing:manage). 201 created, 200 existing; both return the location. */
    async createTerminalLocation(address) {
        const r = await this.request("POST", "/v1/terminal/locations", { address }, true);
        return r.location;
    }
    /**
     * Server creates the PaymentIntent for the held order's total plus the optional tablet tip (#1341); the tablet only
     * gets the client secret. Choose the tip before collecting the card. 409 on a changed total or a different tip.
     */
    async createTerminalPaymentIntent(orderId, receiptEmail, tipCents) {
        const body = { orderId };
        if (receiptEmail)
            body["receiptEmail"] = receiptEmail;
        if (tipCents !== undefined && tipCents > 0)
            body["tipCents"] = Math.round(tipCents);
        return this.request("POST", "/v1/terminal/payment-intents", body, true);
    }
    /** Re-reads Stripe and settles; `paid: false` means not captured yet. */
    async confirmTerminalPaymentIntent(orderId) {
        return this.request("POST", `/v1/terminal/payment-intents/${encodeURIComponent(orderId)}/confirm`, undefined, true);
    }
    // ---- Tips and manager tip out (#1341, #1342) ----
    /**
     * Staff (`orders:manage`). Paper tip adjustment, only when the tenant flag is on. 403 flag off, 409 not adjustable
     * (already captured, reader-tipped or past 48h), 400 invalid.
     */
    async adjustOrderTip(orderId, tipCents) {
        return this.request("PATCH", `/v1/orders/${encodeURIComponent(orderId)}/tip`, { tipCents: Math.round(tipCents) }, true);
    }
    /** Manager PIN. Read-only totals per employee and the drawer position. 401 pin_invalid, 403 manager_required, 400 no_recipients. */
    async previewTipOut(input) {
        return this.request("POST", "/v1/tip-out/preview", input, true);
    }
    /** Manager PIN. Pays out a period once. 409 period_paid / drawer_short, 429 rate_limited. */
    async payOutTips(input) {
        return this.request("POST", "/v1/tip-out/payouts", input, true);
    }
    /** Manager PIN. Appends a signed correction row against an earlier payout. */
    async correctTipOut(input) {
        return this.request("POST", "/v1/tip-out/payouts", input, true);
    }
    async listTipPayouts(from, to) {
        const r = await this.request("GET", `/v1/tip-out/payouts${ClientBase.query({ from, to })}`, undefined, true);
        return r.payouts ?? [];
    }
    /** Absolute URL of the by-employee CSV (bearer header required; use `fetchTipOutCsv` for an authed Blob). */
    tipOutCsvUrl(from, to) {
        return `${this.baseUrl}/v1/tip-out/payouts/export.csv${ClientBase.query({ from, to })}`;
    }
    async fetchTipOutCsv(from, to) {
        const headers = {};
        const token = this.getAccessToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const res = await this.fetchImpl(this.tipOutCsvUrl(from, to), { method: "GET", headers });
        if (!res.ok) {
            let err = {};
            try {
                err = JSON.parse(await res.text());
            }
            catch {
                /* non-JSON error body */
            }
            throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText, err);
        }
        return res.blob();
    }
    // ---- Counter / edge routes server-only (#1411) ----
    /** Manager JWT (orders:manage or payroll:manage). Queues the payout slip on the register printer (#1389). 202 queued, 409 no_printer, 404 unknown payout. */
    async printTipOutSlip(payoutId) {
        return this.request("POST", `/v1/tip-out/payouts/${encodeURIComponent(payoutId)}/print-slip`, undefined, true);
    }
    /** Staff (JWT). Web / DoorDash / Uber Eats orders since `since` (RFC3339, default last 24h), read-only (#1340). 400 bad since. */
    async getOnlineFeed(since) {
        return this.request("GET", `/v1/orders/online-feed${ClientBase.query({ since })}`, undefined, true);
    }
    /** Staff (JWT). Puts the (order, kind) print job back on the poll queue (#1340). 202 queued; kind defaults to kitchen. */
    async reprintOrder(orderId, kind) {
        return this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/reprint`, kind ? { kind } : {}, true);
    }
    /** Device bearer (Pi, #1356). Reports the LAN ip:port. 200 `{status:"ok"}` stored; 204 (undefined) dropped within 30 s. */
    async reportEdgeLanAddress(deviceToken, input) {
        return this.request("POST", "/v1/edge/lan-address", input, false, false, {
            Authorization: `Bearer ${deviceToken}`,
        });
    }
    /** Staff JWT, or a device bearer when `deviceToken` is given (#1356). The tenant's reported LAN addresses. */
    async listEdgeLanAddresses(deviceToken) {
        const headers = deviceToken ? { Authorization: `Bearer ${deviceToken}` } : undefined;
        const r = await this.request("GET", "/v1/edge/lan-addresses", undefined, !deviceToken, false, headers);
        return r.addresses ?? [];
    }
    /** Device bearer (Pi). Idempotent by auditId (#1336). 201 created, 200 replay. 403 pin_invalid / pin_required. */
    async recordEdgeDrawerAudit(deviceToken, input) {
        return this.request("POST", "/v1/edge/drawer-audit", input, false, false, {
            Authorization: `Bearer ${deviceToken}`,
        });
    }
    /** Staff (JWT). PIN-checked non-sale drawer kick (#1336). 202 queued. 403 pin_invalid / pin_required, 409 no_printer, 429 pin_locked. */
    async drawerKickNoSale(input) {
        return this.request("POST", "/v1/drawer/kick", input, true);
    }
    /** Manager (JWT, orders:manage). Drawer audit rows, optional from/to/limit (#1336). */
    async listDrawerAudits(params = {}) {
        const q = ClientBase.query({ from: params.from, to: params.to, limit: params.limit });
        const r = await this.request("GET", `/v1/drawer-audit${q}`, undefined, true);
        return r.audits ?? [];
    }
    /** JWT + content write. Recent marketing QR slip jobs for the tenant, newest first (#1139). */
    async listStaffSlipPrintJobs() {
        const r = await this.request("GET", "/v1/slip-print-jobs", undefined, true);
        return r.jobs ?? [];
    }
    /** JWT + content write. Queues N copies of the QR slip for the tenant's kitchen Pi (#1139). 201 created. */
    async createStaffSlipPrintJob(input) {
        return this.request("POST", "/v1/slip-print-jobs", { copies: input.copies, title: input.title ?? "", qrUrl: input.qrUrl ?? "" }, true);
    }
    /** JWT + content write. Stops a queued or printing slip job after the current copy (#1139). */
    async cancelStaffSlipPrintJob(id) {
        return this.request("POST", `/v1/slip-print-jobs/${encodeURIComponent(id)}/cancel`, undefined, true);
    }
    // ---- Branded reader UI (#1351) ----
    /** Owner (billing:manage) or platform admin. */
    async setReaderUi(slug, input) {
        return this.request("PUT", `${this.siteBase(slug)}/terminal/reader-ui`, input, true);
    }
    /** Staff. 204. */
    async clearReaderDisplay(slug, readerId) {
        await this.request("POST", `${this.siteBase(slug)}/terminal/readers/${encodeURIComponent(readerId)}/clear-display`, undefined, true);
    }
    // ---- Timeclock (#1203) ----
    /**
     * PIN punch (staff JWT or paired device token). Idempotent on `input.id`, so a queued
     * punch can be replayed safely. A 409 punch_sequence throws PunchSequenceError with `allowed`.
     */
    async punchClock(input) {
        try {
            return await this.request("POST", "/v1/timeclock/punch", input, true);
        }
        catch (err) {
            if (err instanceof ApiError && err.status === 409 && err.code === "punch_sequence") {
                const body = (err.body ?? {});
                throw new PunchSequenceError(err.message, Array.isArray(body.allowed) ? body.allowed : []);
            }
            throw err;
        }
    }
    async clockStatus() {
        const r = await this.request("GET", "/v1/timeclock/status", undefined, true);
        return r.employees ?? [];
    }
    async listTimesheets(period) {
        const r = await this.request("GET", `/v1/timeclock/timesheets${ClientBase.query({ period })}`, undefined, true);
        return { period: r.period, timesheets: r.timesheets ?? [] };
    }
    async addPunch(input) {
        return this.request("POST", "/v1/timeclock/punches", input, true);
    }
    async correctPunch(id, patch) {
        return this.request("PATCH", `/v1/timeclock/punches/${encodeURIComponent(id)}`, patch, true);
    }
    timeclockExportUrl(period, variant = "summary") {
        return `${this.baseUrl}/v1/timeclock/export.csv${ClientBase.query({ period, variant })}`;
    }
    async exportTimeclockCsv(period, variant = "summary") {
        const headers = {};
        const token = this.getAccessToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const res = await this.fetchImpl(this.timeclockExportUrl(period, variant), { method: "GET", headers });
        const text = await res.text();
        if (!res.ok) {
            let err = {};
            try {
                err = JSON.parse(text);
            }
            catch {
                /* non-JSON */
            }
            throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText);
        }
        return text;
    }
    async rollupTimesheets(period) {
        return this.request("POST", `/v1/timeclock/rollup${ClientBase.query({ period })}`, undefined, true);
    }
    // --- #772 shared QR table tabs ---
    /** Staff when no guest token is given (bearer attached); guest otherwise (header only). */
    tabCall(method, path, body, opts = {}, extra = {}) {
        const headers = { ...extra };
        if (opts.guestToken)
            headers["X-Tab-Guest-Token"] = opts.guestToken;
        return this.request(method, path, body, !opts.guestToken, false, headers);
    }
    /** Staff. Opens the tab for a table; the result carries the QR `joinToken`. 409 `tab_already_open` when the table already has one. */
    async openTableTab(slug, tableCode) {
        return this.request("POST", `${this.siteBase(slug)}/tabs`, { tableCode }, true);
    }
    /** Staff. Open and splitting tabs of the site. */
    async listOpenTableTabs(slug) {
        const r = await this.request("GET", `${this.siteBase(slug)}/tabs`, undefined, true);
        return r.tabs;
    }
    /** Public. The QR token travels in the body. Keep `guestToken`; it is shown once. */
    async joinTableTab(tabId, joinToken, name = "") {
        return this.request("POST", `/v1/tabs/${encodeURIComponent(tabId)}/join`, { joinToken, name });
    }
    /** Live state. Poll it; `version` changes on every write. */
    async getTableTab(tabId, opts) {
        return this.tabCall("GET", `/v1/tabs/${encodeURIComponent(tabId)}`, undefined, opts);
    }
    /** `version` is the last version read (sent as If-Match). A stale one rejects with ApiError 409 `version_conflict`: re-read and retry. */
    async addTableTabItem(tabId, version, item, opts) {
        return this.tabCall("POST", `/v1/tabs/${encodeURIComponent(tabId)}/items`, item, opts, { "If-Match": `"${version}"` });
    }
    /** Fixes the shares. `seat` takes no parts; `item` takes lineIds per part; `amount` takes amountCents per part. Parts must sum to the tab total (400 otherwise). */
    async splitTableTab(tabId, version, mode, parts = [], opts) {
        return this.tabCall("POST", `/v1/tabs/${encodeURIComponent(tabId)}/splits`, { mode, parts }, opts, { "If-Match": `"${version}"` });
    }
    /** Creates the split's PaymentIntent; the response carries `clientSecret` for Stripe.js. The split stays `pending` until the Stripe webhook marks it paid (poll `getTableTab`). 409 `connect_not_ready` / 503 `checkout_disabled`. */
    async settleTableTabSplit(tabId, splitId, opts) {
        return this.tabCall("POST", `/v1/tabs/${encodeURIComponent(tabId)}/splits/${encodeURIComponent(splitId)}/settle`, undefined, opts);
    }
    /** Staff. 409 until every split is paid. */
    async closeTableTab(tabId) {
        return this.tabCall("POST", `/v1/tabs/${encodeURIComponent(tabId)}/close`, undefined);
    }
    // Item attributes, order edits, refunds (#1416 #1422). All staff-authorised.
    async getMenuAttributes(slug) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/menu/attributes`, undefined, true);
    }
    async putMenuAttribute(slug, id, body) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/menu/attributes/${encodeURIComponent(id)}`, body, true);
    }
    /** Sets the item's attribute edges. 409 `attribute_cycle` on a cycle; 400 `validation_error` on a duplicate path. Returns the re-materialised tree. */
    async putMenuItemAttributes(slug, itemId, body) {
        return this.request("PUT", `/v1/sites/${encodeURIComponent(slug)}/menu/items/${encodeURIComponent(itemId)}/attributes`, body, true);
    }
    /** Per-location 86 / price / station override on a shared menu. */
    async patchMenuOverrides(slug, body) {
        return this.request("PATCH", `/v1/sites/${encodeURIComponent(slug)}/menu/overrides`, body, true);
    }
    /** Adds lines to an open tab (no PIN). `baseRev` is not compared: a stale rev is rebased (up to 5 attempts). 409 `rev_conflict` only if every attempt loses; 409 `order_paid` on a paid order. */
    async addOrderLines(orderId, body) {
        return this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/lines`, body, true);
    }
    /** Edits or removes sent lines; `managerPin` is required once the order has printed. */
    async patchOrderLines(orderId, body) {
        return this.request("PATCH", `/v1/orders/${encodeURIComponent(orderId)}/lines`, body, true);
    }
    /** Voids an unpaid order; manager PIN required. */
    async voidOrder(orderId, body) {
        return this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/void`, body, true);
    }
    /** Per-unit or per-attribute refund; manager PIN required. */
    async refundOrderItems(orderId, body) {
        return this.request("POST", `/v1/orders/${encodeURIComponent(orderId)}/refund-items`, body, true);
    }
    /** Append-only track record plus already-refunded paths. */
    async getOrderEvents(orderId) {
        return this.request("GET", `/v1/orders/${encodeURIComponent(orderId)}/events`, undefined, true);
    }
}
//# sourceMappingURL=dine.js.map